import * as React from 'react';
import Dots from '../../assets/icons/dots-three-vertical--small.svg';
import EmptyStar from '../../assets/icons/star-outlined--yellow.svg';
import FilledStar from '../../assets/icons/star-filled--yellow.svg';
import type { DocumentIconType } from './helpers';
import { documentIcons } from './helpers';
import type { Group } from '../../containers/GroupsList/groupsSlice/types';
import { HighlightSearchTerm } from '../Checklists/HighlightSearchTerm/HighlightSearchTerm';
import { getDocument } from '../../apis/mediaAPI';
import {
  SContainer,
  SGrowDiv,
  SMoreOptionBtn,
  InitialsIcon,
  InitialsText,
  PictureIcon,
} from './DocumentItem.styles';

type IDocumentItemProps = {
  group?: Group;
  title: string;
  subTitle?: string;
  itemId: number;

  iconVariant: DocumentIconType;

  boldTitle?: boolean;
  pdfLink?: string;

  onMoreOptionClick?: (itemId: number) => void;
  onFavClick?: (itemId: number, newFavValue: boolean) => void;
  onClick?: (itemId: number) => void;
  favorite: boolean;
  searchTerm?: string;
};

export const DocumentItem = ({
  group,
  itemId,
  subTitle,
  title,
  onMoreOptionClick,
  onClick,
  boldTitle,
  iconVariant,
  pdfLink,
  favorite,
  searchTerm,
  onFavClick,
}: IDocumentItemProps) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onMoreOptionClick) onMoreOptionClick(itemId);
  };

  const openDocument = async () => {
    if (onClick && !pdfLink) {
      onClick(itemId);
      return;
    }

    if (pdfLink) {
      try {
        const blob = await getDocument(pdfLink);
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Revoke blob URL after giving new tab time to load the data.
        // This releases memory from the app tab; the new tab retains its own copy.
        setTimeout(() => window.URL.revokeObjectURL(url), 500);
      } catch (error) {
        console.error('Error opening document:', error);
      }
    }
  };

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!itemId) return;
    onFavClick?.(itemId, !favorite);
  };

  const getNameInitials = () => {
    if (!group?.name) {
      return '';
    }

    const nameArray = group.name.split(' ');
    if (nameArray.length === 1) {
      return nameArray[0].charAt(0);
    } else if (nameArray.length > 1) {
      return nameArray[0].charAt(0) + nameArray[1].charAt(0);
    } else {
      return '';
    }
  };

  const getInitialsIcon = () => (
    <InitialsIcon role="initialsIcon">
      <InitialsText role="initialsText">{getNameInitials()}</InitialsText>
    </InitialsIcon>
  );
  const getPictureIcon = () => (
    <PictureIcon
      role="pictureIcon"
      src={group?.imageFileName || documentIcons[iconVariant]}
      alt={iconVariant}
    />
  );

  return (
    <SContainer
      $boldTitle={boldTitle}
      onClick={openDocument}
      role={`documentItem-${title}`}
      id={`documentItem-${itemId}`}
    >
      {group ? (
        group.imageFileName ? (
          getPictureIcon()
        ) : (
          getInitialsIcon()
        )
      ) : (
        <img
          role="docIcon"
          className={`icon ${iconVariant}`}
          src={documentIcons[iconVariant]}
          alt={iconVariant}
        />
      )}

      <SGrowDiv role="infoContainer">
        <p className="title" role="title">
          {HighlightSearchTerm(title, searchTerm)}
        </p>
        <p className={'subTitle'} role="subtitle">
          {subTitle}
        </p>
      </SGrowDiv>
      <img
        className="favIcon"
        src={favorite ? FilledStar : EmptyStar}
        onClick={toggleFavorite}
        alt=""
        role="favoriteIcon"
      />
      {onMoreOptionClick && (
        <SMoreOptionBtn onClick={handleClick} role="moreOptions">
          <img role="icon" src={Dots} alt="" />
        </SMoreOptionBtn>
      )}
    </SContainer>
  );
};
